import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getMaterialPreviewFingerprint,
  getMaterialSpherePreviewSize,
  invalidateMaterialSpherePreview,
  requestMaterialSpherePreview,
} from '../../utils/ThreeJsUtils/materialSpherePreview';

function isMaterialTransparent(material) {
  if (!material) return false;
  const opacity =
    typeof material.opacity === 'number' ? material.opacity : 1;
  return (
    !!material.transparent ||
    opacity < 0.999 ||
    !!material.alphaMap ||
    (typeof material.transmission === 'number' && material.transmission > 0)
  );
}

/**
 * Small lit sphere showing a Three.js material.
 * When `live`, re-renders as material properties change (in-place Three.js edits).
 * Transparent materials use a plain dark backdrop so opacity stays visible.
 */
const MaterialSpherePreview = React.memo(function MaterialSpherePreview({
  material,
  size = 40,
  className = '',
  live = false,
}) {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [dataUrl, setDataUrl] = useState(null);
  const [fallbackColor, setFallbackColor] = useState('#555555');
  const [liveTick, setLiveTick] = useState(0);
  const lastFingerprintRef = useRef('');
  const renderSize = getMaterialSpherePreviewSize();

  const transparent = useMemo(
    () => isMaterialTransparent(material),
    // liveTick refreshes when opacity/transparent change during live edits
    [material, material?.uuid, material?.opacity, material?.transparent, liveTick]
  );

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    let shown = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (!shown && entries.some((e) => e.isIntersecting)) {
          shown = true;
          setVisible(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin: '48px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Selected material: watch Three.js property mutations and refresh the sphere.
  useEffect(() => {
    if (!live || !material) return undefined;

    lastFingerprintRef.current = getMaterialPreviewFingerprint(material);

    const poll = () => {
      const next = getMaterialPreviewFingerprint(material);
      if (next !== lastFingerprintRef.current) {
        lastFingerprintRef.current = next;
        invalidateMaterialSpherePreview(material);
        setLiveTick((t) => t + 1);
        if (material.color?.isColor) {
          setFallbackColor(`#${material.color.getHexString()}`);
        }
      }
    };

    poll();
    const id = setInterval(poll, 180);
    return () => clearInterval(id);
  }, [live, material, material?.uuid]);

  useEffect(() => {
    if (!visible || !material) return undefined;

    let cancelled = false;

    if (material.color?.isColor) {
      setFallbackColor(`#${material.color.getHexString()}`);
    }

    requestMaterialSpherePreview(material).then((url) => {
      if (!cancelled && url) setDataUrl(url);
    });

    return () => {
      cancelled = true;
    };
    // liveTick forces refresh when fingerprint changes during live edits.
  }, [visible, material, material?.uuid, live, liveTick]);

  return (
    <span
      ref={wrapRef}
      className={`rounded-full border border-gray-600 shrink-0 overflow-hidden ${className}`}
      style={{
        width: size,
        height: size,
        display: 'inline-block',
        background: transparent ? '#202124' : fallbackColor,
      }}
      aria-hidden
    >
      {dataUrl ? (
        <img
          src={dataUrl}
          alt=""
          width={renderSize}
          height={renderSize}
          draggable={false}
          style={{
            width: size,
            height: size,
            display: 'block',
            objectFit: 'cover',
          }}
        />
      ) : null}
    </span>
  );
});

export default MaterialSpherePreview;
